import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/joy';
import {
  formatDateGb,
  invoiceDueDate,
  paymentTermsLabel,
  paymentTermsPreset,
  resolveInvoicePaymentSchedule,
  todayDateOnly,
} from '../utils/invoicePaymentTerms';

const PRESET_OPTIONS: Array<{ value: string; days: number; label: string }> = [
  { value: '0', days: 0, label: 'Due on receipt' },
  { value: '7', days: 7, label: '7 days' },
  { value: '14', days: 14, label: '14 days' },
  { value: '30', days: 30, label: '30 days' },
  { value: 'custom', days: -1, label: 'Custom' },
];

export function InvoicePaymentTermsModal({
  open,
  locked,
  invoiceRole,
  issuedAt,
  createdAt,
  paymentTermsInDays,
  onClose,
  onSave,
}: {
  open: boolean;
  locked?: boolean;
  invoiceRole?: string | null;
  issuedAt?: string | null;
  createdAt?: string | null;
  paymentTermsInDays?: number | null;
  onClose: () => void;
  onSave: (next: { invoiceDate: string; paymentTermsInDays: number }) => Promise<void>;
}) {
  const initial = useMemo(
    () => resolveInvoicePaymentSchedule({
      invoiceRole,
      issuedAt,
      createdAt,
      paymentTermsInDays,
      locked,
    }),
    [createdAt, invoiceRole, issuedAt, locked, paymentTermsInDays],
  );
  const [invoiceDate, setInvoiceDate] = useState(initial.invoiceDate);
  const [preset, setPreset] = useState(String(paymentTermsPreset(initial.paymentTermsInDays)));
  const [customDays, setCustomDays] = useState(
    paymentTermsPreset(initial.paymentTermsInDays) === 'custom' ? String(initial.paymentTermsInDays) : '10',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setInvoiceDate(initial.invoiceDate || todayDateOnly());
    setPreset(String(paymentTermsPreset(initial.paymentTermsInDays)));
    setCustomDays(
      paymentTermsPreset(initial.paymentTermsInDays) === 'custom' ? String(initial.paymentTermsInDays) : '10',
    );
    setError(null);
  }, [initial, open]);

  const termsDays = preset === 'custom' ? Math.max(0, Math.floor(Number(customDays) || 0)) : Number(preset);
  const dueDate = invoiceDate ? invoiceDueDate(invoiceDate, termsDays) : '';

  const save = async () => {
    if (locked) return;
    if (!invoiceDate) {
      setError('Choose an invoice date.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({ invoiceDate, paymentTermsInDays: termsDays });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save payment terms');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { if (!saving) onClose(); }}>
      <ModalDialog sx={{ width: 'min(420px, 95vw)' }}>
        <DialogTitle>Edit payment terms</DialogTitle>
        <DialogContent>
          <Stack gap={1.5} sx={{ mt: 0.5 }}>
            {locked && (
              <Alert color="neutral">
                Invoice date and payment terms are locked after this invoice was issued to FreeAgent.
              </Alert>
            )}
            <FormControl>
              <FormLabel>Invoice date</FormLabel>
              <Input
                type="date"
                value={invoiceDate}
                disabled={locked || saving}
                onChange={(event) => setInvoiceDate(event.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Payment terms</FormLabel>
              <RadioGroup
                value={preset}
                onChange={(event) => setPreset(event.target.value)}
                sx={{ gap: 0.5 }}
              >
                {PRESET_OPTIONS.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    label={option.label}
                    disabled={locked || saving}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            {preset === 'custom' && (
              <FormControl>
                <FormLabel>Custom days</FormLabel>
                <Input
                  type="number"
                  slotProps={{ input: { min: 0, step: 1 } }}
                  value={customDays}
                  disabled={locked || saving}
                  onChange={(event) => setCustomDays(event.target.value)}
                />
              </FormControl>
            )}
            <Typography level="body-sm">
              Due date: {dueDate ? formatDateGb(dueDate) : '—'}
            </Typography>
            <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
              {formatDateGb(invoiceDate)} · {paymentTermsLabel(termsDays)}
            </Typography>
            {error && <Alert color="danger">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" disabled={saving} onClick={onClose}>Cancel</Button>
          <Button loading={saving} disabled={locked} onClick={() => { void save(); }}>
            Save
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
